import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

const WORDS = ['Dream Home', 'Vision', 'Future', 'Legacy', 'Success Story']

// Simple CountUp component
function AnimatedNumber({ end, duration = 2, inView }) {
  const [count, setCount] = useState(0)
  const started = useRef(false)

  useEffect(() => {
    if (!inView || started.current) return
    started.current = true
    const steps = 60
    const step = end / steps
    let current = 0
    const interval = setInterval(() => {
      current += step
      if (current >= end) {
        setCount(end)
        clearInterval(interval)
      } else {
        setCount(Math.floor(current))
      }
    }, (duration * 1000) / steps)
    return () => clearInterval(interval)
  }, [inView, end, duration])

  return <span>{count}</span>
}

export default function Hero() {
  const [wordIdx, setWordIdx] = useState(0)
  const [displayed, setDisplayed] = useState('')
  const [deleting, setDeleting] = useState(false)
  const { ref: statsRef, inView } = useInView({ threshold: 0.3, triggerOnce: true })

  useEffect(() => {
    const word = WORDS[wordIdx]
    let timeout
    if (!deleting && displayed.length < word.length) {
      timeout = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 90)
    } else if (!deleting && displayed.length === word.length) {
      timeout = setTimeout(() => setDeleting(true), 1800)
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 55)
    } else if (deleting && displayed.length === 0) {
      setDeleting(false)
      setWordIdx((wordIdx + 1) % WORDS.length)
    }
    return () => clearTimeout(timeout)
  }, [displayed, deleting, wordIdx])

  const stats = [
    { num: 30, suffix: '+', label: 'Projects Done' },
    { num: 30, suffix: '+', label: 'Happy Clients' },
    { num: 7, suffix: '+', label: 'Years Experience' },
    { num: 100, suffix: '+', label: 'Plots Available' },
  ]

  const scrollTo = (selector) => {
    const el = document.querySelector(selector)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="hero" id="home">
      <div className="hero-bg-img">
        <picture>
          <source media="(max-width: 768px)" srcSet="/images/hero_bg-mobile.webp" />
          <img src="/images/hero_bg.webp" alt="Assu Buildtech construction project in Delhi NCR and Haryana" loading="eager" fetchpriority="high" />
        </picture>
      </div>
      <div className="hero-gradient" />

      <div className="container">
        <div className="hero-content" style={{ marginTop: '5vh' }}>
          {/* Badge */}
          <motion.div
            className="hero-chip"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="chip-dot" />
            Haryana &amp; Delhi NCR's Trusted Builder
          </motion.div>

          {/* Title */}
          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <span className="hero-title-main">We Build Your</span>
            <span className="line2">
              <span className="hero-typewriter">{displayed}</span>
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            className="hero-desc"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            style={{ marginBottom: 10 }}
          >
            From dream plots to landmark constructions — Assu Buildtech delivers excellence
            in architecture, construction, interior design, and real estate across Haryana &amp; Delhi NCR.
          </motion.p>

          {/* Trust/Urgency Element */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            style={{ color: '#f5c518', fontSize: '0.95rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 30 }}
          >
            <i className="fas fa-shield-alt" /> 100% Client Satisfaction &bull; No Hidden Charges
          </motion.div>

          {/* Buttons */}
          <motion.div
            className="hero-btns"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
          >
            <a className="btn-gold" href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('#contact') }} style={{ padding: '16px 32px', fontSize: '1.1rem' }}>
              <span>Get Free Consultation</span>
              <i className="fas fa-arrow-right" />
            </a>
            <a className="btn-outline" href="tel:+918685838583" style={{ padding: '16px 32px', fontSize: '1.1rem' }}>
              <i className="fas fa-phone-alt" />
              Call Now
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="hero-stats"
            ref={statsRef}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            style={{ marginTop: 60 }}
          >
            {stats.map((s, i) => (
              <div className="hero-stat" key={i}>
                <div className="stat-number">
                  <AnimatedNumber end={s.num} duration={2} inView={inView} />
                  {s.suffix}
                </div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="hero-scroll">
        <div className="scroll-wheel">
          <div className="scroll-dot" />
        </div>
        <span>Scroll Down</span>
      </div>
    </section>
  )
}
